import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router";

import type { GroupChatInfoDto, UpdateGroupChatRequest, UserDto } from "@/api/types";
import { ConfirmModal } from "@/components/ConfirmModal/ConfirmModal";
import { Form } from "@/components/Form/Form";
import { type FormImage } from "@/components/ImageInput/useImageInput";
import type { ModalBaseProps } from "@/components/Modal/Modal";
import { Modal } from "@/components/Modal/Modal";
import { useUploadImage } from "@/hooks/Network/useUploadImage";
import { useBool } from "@/hooks/useBool";
import { useSlug } from "@/hooks/useSlug";
import { chatMutations } from "@/queries/chats/mutations";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

import { EditGroupChatModalAddMembersView } from "./EditGroupChatModalAddMembersView";
import { EditGroupChatModalInfoView } from "./EditGroupChatModalInfoView";

type EditGroupChatModalProps = ModalBaseProps & {
  chatId: string;
  groupInfo: GroupChatInfoDto;
};

export interface EditGroupChatFormValues {
  groupName: string;
  groupImage: FormImage[];
  newMembers: UserDto[];
}

enum Step {
  GroupInfo,
  AddMembers,
}

export function EditGroupChatModal({
  isOpened,
  onOpenChange,
  chatId,
  groupInfo,
}: EditGroupChatModalProps): React.ReactNode {
  const { t } = useTranslation();
  const slug = useSlug();

  const navigate = useNavigate();

  const [step, setStep] = useState<Step>(Step.GroupInfo);
  const [userToRemove, setUserToRemove] = useState<UserDto | null>(null);
  const [isLeaveModalOpen, leaveModalHandler] = useBool();

  const { uploadFormImage, isUploadingImage } = useUploadImage();
  const formMethods = useForm<EditGroupChatFormValues>({
    defaultValues: { groupName: groupInfo.name, groupImage: groupInfo.image ? [groupInfo.image] : [], newMembers: [] },
  });

  useEffect(() => {
    formMethods.reset({
      groupName: groupInfo.name,
      groupImage: groupInfo.image ? [groupInfo.image] : [],
      newMembers: [],
    });
  }, [formMethods, groupInfo]);

  const leaveGroupChatMutation = chatMutations.useLeaveGroupChat();
  const removeGroupChatMemberMutation = chatMutations.useRemoveGroupChatMember(chatId);
  const updatGroupChatMutation = chatMutations.useUpdateGroupChat(chatId);

  const onSubmit = async (data: EditGroupChatFormValues) => {
    const payload: UpdateGroupChatRequest = { name: data.groupName.trim() };

    if (data.groupImage.length > 0) {
      const uploadedImage = await uploadFormImage(data.groupImage[0]);

      payload.imageId = uploadedImage?.id;
    }

    void updatGroupChatMutation.mutateAsync(payload, {
      onSuccess: () => {
        onOpenChange(false);
      },
    });
  };

  const handlePrevStep = (step: Step) => {
    setStep(step);
  };

  const handleNextStep = (step: Step) => {
    setStep(step);
  };

  let title = "";
  switch (step) {
    case Step.GroupInfo:
      title = t("component.group-chat-info-modal.title");
      break;
    case Step.AddMembers:
      title = t("page.chats.modal.new-chat.group-chat.select-member.title");
      break;
  }

  return (
    <>
      <Modal.Root
        {...{ title, isOpened, onOpenChange }}
        onExitComplete={() => {
          formMethods.reset();
          leaveModalHandler.setFalse();
          setStep(0);
        }}
      >
        <Modal.Body>
          <Form.Root fullWidth maxHeight="parent" {...{ formMethods, onSubmit }}>
            {step === Step.GroupInfo && (
              <EditGroupChatModalInfoView
                canLeave={groupInfo.canLeave}
                groupChatId={chatId}
                isModalOpen={isOpened}
                isUpdating={updatGroupChatMutation.isPending || leaveGroupChatMutation.isPending || isUploadingImage}
                onAddMembers={() => handleNextStep(Step.AddMembers)}
                onLeaveGroup={leaveModalHandler.setTrue}
              />
            )}
            {step === Step.AddMembers && (
              <EditGroupChatModalAddMembersView
                onNavigateBack={() => handlePrevStep(Step.GroupInfo)}
                isOpen={isOpened}
                {...{ chatId }}
              />
            )}
          </Form.Root>
        </Modal.Body>
      </Modal.Root>
      <ConfirmModal
        title={t("component.group-chat-leave-modal.title")}
        description={t("component.group-chat-leave-modal.description")}
        isLoading={leaveGroupChatMutation.isPending}
        tone="warning"
        onOpenChange={leaveModalHandler.set}
        onReject={leaveModalHandler.setFalse}
        rejectBtnProps={{
          "data-testid": "leave-group-chat-modal-cancel",
        }}
        onResolve={() =>
          leaveGroupChatMutation.mutateAsync(chatId, {
            onSuccess: () => {
              onOpenChange(false);
              void navigate(routes.chats.list({ slug: slug }));
            },
          })
        }
        resolveBtnProps={{
          "data-testid": "leave-group-chat-modal-confirm",
          text: t("common.action.confirm"),
        }}
        isOpened={isLeaveModalOpen}
        shouldCloseOnEsc
      />
      <ConfirmModal
        title={t("component.group-chat-remove-user-modal.title")}
        description={t("component.group-chat-remove-user-modal.description", { user: userToRemove?.fullName ?? "" })}
        isLoading={removeGroupChatMemberMutation.isPending}
        tone="warning"
        onOpenChange={(state) => {
          if (!state) {
            setUserToRemove(null);
          }
        }}
        onReject={() => setUserToRemove(null)}
        rejectBtnProps={{
          "data-testid": "chat-remove-user-modal-cancel",
        }}
        onResolve={() => {
          if (!userToRemove) {
            return;
          }

          removeGroupChatMemberMutation.mutate(
            { userId: userToRemove.id },
            {
              onSuccess: () => setUserToRemove(null),
            },
          );
        }}
        resolveBtnProps={{
          "data-testid": "chat-remove-user-modal-confirm",
          text: t("common.action.remove"),
        }}
        isOpened={!!userToRemove}
        shouldCloseOnEsc
      />
    </>
  );
}
